import { inject, Injectable } from '@angular/core';

import { Observable, of, switchMap } from 'rxjs';

import { filterBoolean } from '@core/common-legacy';
import { AssignMembersToJobsService } from '@core/tellent-assign-members-to-jobs-smart-ui';
import { type DialogConfigOptions, DialogService, DialogWidth } from '@core/user-interface-legacy';
import { useProtectedControlsCallback } from '@core/user-interface-routing-logic';

import { AddMemberDialogComponent } from './add-member-dialog.component';

@Injectable({ providedIn: 'root' })
export class AddMemberService {
  private readonly dialogService = inject(DialogService);
  private readonly assignMembersService = inject(AssignMembersToJobsService);
  private readonly protectedControls = useProtectedControlsCallback();

  public openAddMemberDialog(config: DialogConfigOptions = {}) {
    const ref = this.dialogService.openCustom(AddMemberDialogComponent, {
      width: DialogWidth.Small,
      ...config,
    });

    ref.afterClosed
      .pipe(
        filterBoolean,
        switchMap(dialogResult => {
          if (dialogResult.shouldOpenSecondStep) {
            return this.assignMembersService.openAssignMembersDialog(dialogResult);
          }
          return of(false);
        }),
      )
      .subscribe();

    return ref;
  }

  public openAddMemberDialogAsObservable(config: DialogConfigOptions = {}): Observable<any> {
    return this.dialogService
      .openCustom(AddMemberDialogComponent, {
        width: DialogWidth.Small,
        closeGuard: () => this.protectedControls(() => true),
        ...config,
      })
      .afterClosed.pipe(
        filterBoolean,
        switchMap(dialogResult => {
          if (dialogResult.shouldOpenSecondStep) {
            return this.assignMembersService.openAssignMembersDialog(dialogResult);
          }
          return of(false);
        }),
      );
  }

  public startInviteMemberFlow(config: DialogConfigOptions = {}): Observable<any> {
    return this.dialogService
      .openCustom(AddMemberDialogComponent, {
        width: DialogWidth.Small,
        closeGuard: () => this.protectedControls(() => true),
        ...config,
      })
      .afterClosed.pipe(
        filterBoolean,
        switchMap(dialogResult => {
          if (dialogResult.shouldOpenSecondStep) {
            return this.assignMembersService.openAssignMembersDialog(dialogResult);
          }
          return of(false);
        }),
      );
  }

  public openAddMemberDialogAsTellentClientCompatibleStream(
    config: DialogConfigOptions = {},
  ): Observable<any> {
    return this.dialogService
      .openCustom(AddMemberDialogComponent, { width: DialogWidth.Small, ...config })
      .afterClosed.pipe(
        switchMap(dialogResult => {
          if (dialogResult && dialogResult.shouldOpenSecondStep) {
            return this.assignMembersService.openAssignMembersDialog(dialogResult);
          }

          return of(dialogResult);
        }),
      );
  }
}
